// Browser SigV4 presign of the voice runtime WebSocket URL (sparky-voice spec,
// task 2.3; Req 12.2/12.3/12.4, NFR3.6).
//
// The browser can't set an Authorization header on a WebSocket handshake, so the
// AgentCore Runtime `/ws` endpoint is reached with a SigV4 QUERY-presigned URL: we
// sign an https GET for the runtime's connect URL with temporary AWS credentials
// (from a Cognito Identity Pool) and open the result as `wss://`. This matches the
// confirmed connect recipe from the PoC (agent/poc/task-1.3-findings.md §3):
//   host   bedrock-agentcore.<region>.amazonaws.com
//   path   /runtimes/<url-encoded full runtime ARN>/ws
//   query  qualifier=DEFAULT & X-Amzn-Bedrock-AgentCore-Runtime-Session-Id=<id>
//   SigV4  service `bedrock-agentcore`, region = the runtime's home region.
//
// This module splits cleanly into two halves so the tricky part is unit-testable
// without any network, DOM or SDK (task 2.4):
//   • buildPresignedVoiceWsUrl(...) — PURE. Deterministic SigV4 query-presign given
//     region/arn/session/credentials/clock. No I/O. Tested under `node --test`.
//   • presignVoiceWebSocketUrl()    — the network wrapper: fetch temporary creds
//     from the Identity Pool (reusing getValidIdToken), then call the pure builder.
//
// Dependency choice: the pure builder implements the SigV4 query-presign directly
// on top of `globalThis.crypto.subtle` (HMAC-SHA256 + SHA-256), rather than pulling
// in @aws-sdk/signature-v4 + @smithy/protocol-http + @aws-sdk/client-cognito-identity.
// Those aren't current deps (only transitive scraps of @aws-sdk/@smithy from the
// Cognito SDK are installed), they're heavy in the esbuild bundle, and — most
// importantly — the rest of ui/src/lib deliberately keeps its pure logic
// dependency-free and cross-environment (see token.js, voice.js) so it runs
// identically in the browser and under `node --test`. Web Crypto is present in both
// modern browsers and Node 18+, so the builder stays pure, small and testable.
//
// Security (Req 12.4 / NFR3.6): the presigned URL is SHORT-LIVED (see
// VOICE_PRESIGN_EXPIRY_SECONDS) and the runtime rejects an expired/invalid
// signature. We never log the full signed URL or the credential values — only
// masked fragments — mirroring the sample. No secrets live in source.

import { getValidIdToken } from '../auth.js';
import { getVoiceConfig } from '../config.js';

// Short presigned-URL lifetime. Req 12.4 wants ~60s, not the sample's 3600s: the
// URL only has to survive from presign to the WebSocket handshake, so a tight
// window minimises the blast radius of a leaked URL. Named constant so it's easy to
// find and tune.
export const VOICE_PRESIGN_EXPIRY_SECONDS = 60;

const SERVICE = 'bedrock-agentcore';
const SIGV4_ALGORITHM = 'AWS4-HMAC-SHA256';
const SESSION_ID_PARAM = 'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id';

// --- low-level crypto (Web Crypto; browser + Node 18+) ----------------------

function subtle() {
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    throw new Error('Web Crypto (crypto.subtle) is unavailable; cannot presign the voice URL');
  }
  return c.subtle;
}

const utf8 = (s) => new TextEncoder().encode(s);

function toHex(buffer) {
  const bytes = new Uint8Array(buffer);
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}

async function sha256Hex(message) {
  const data = typeof message === 'string' ? utf8(message) : message;
  return toHex(await subtle().digest('SHA-256', data));
}

async function hmac(key, message) {
  const keyData = typeof key === 'string' ? utf8(key) : key;
  const cryptoKey = await subtle().importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await subtle().sign('HMAC', cryptoKey, utf8(message)));
}

// --- SigV4 helpers ----------------------------------------------------------

// RFC 3986 encoding for query keys/values (AWS requires the stricter form that
// also escapes ! ' ( ) *, which encodeURIComponent leaves alone).
function rfc3986(str) {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

// SigV4 canonical query string: params sorted by key, both key and value encoded.
function canonicalQuery(params) {
  return Object.keys(params)
    .sort()
    .map((k) => `${rfc3986(k)}=${rfc3986(params[k])}`)
    .join('&');
}

// yyyymmdd'T'hhmmss'Z' (amz-date) and yyyymmdd (datestamp) from an epoch-ms clock.
function amzDates(nowMs) {
  const iso = new Date(nowMs).toISOString().replace(/[:-]|\.\d{3}/g, '');
  return { amzDate: iso, dateStamp: iso.slice(0, 8) };
}

async function signingKey(secretAccessKey, dateStamp, region, service) {
  const kDate = await hmac(`AWS4${secretAccessKey}`, dateStamp);
  const kRegion = await hmac(kDate, region);
  const kService = await hmac(kRegion, service);
  return hmac(kService, 'aws4_request');
}

// --- pure presign builder (task 2.4 tests this) -----------------------------

/**
 * Deterministically SigV4 query-presign the voice runtime WebSocket URL.
 *
 * PURE: given the same inputs (including `nowMs`) it always returns the same URL.
 * No network, no DOM, no SDK — only Web Crypto. Callers that want a fresh signature
 * pass the current clock; tests pass a fixed one.
 *
 * @param {object} args
 * @param {string} args.region            runtime home region, e.g. 'eu-west-2'
 * @param {string} args.runtimeArn        FULL AgentCore runtime ARN
 * @param {string} args.sessionId         session id (>= 33 chars per AWS)
 * @param {object} args.credentials       { accessKeyId, secretAccessKey, sessionToken }
 * @param {number} [args.expiresIn]       URL lifetime in seconds (default 60)
 * @param {number} [args.nowMs]           clock override (epoch ms) for determinism
 * @returns {Promise<string>} a `wss://…` URL ready to open
 */
export async function buildPresignedVoiceWsUrl({
  region,
  runtimeArn,
  sessionId,
  credentials,
  expiresIn = VOICE_PRESIGN_EXPIRY_SECONDS,
  nowMs = Date.now(),
}) {
  if (!region) throw new Error('presign: region is required');
  if (!runtimeArn) throw new Error('presign: runtimeArn is required');
  if (!sessionId) throw new Error('presign: sessionId is required');
  const { accessKeyId, secretAccessKey, sessionToken } = credentials || {};
  if (!accessKeyId || !secretAccessKey || !sessionToken) {
    throw new Error('presign: temporary credentials are incomplete');
  }

  const host = `${SERVICE}.${region}.amazonaws.com`;
  // The full ARN is URL-encoded into the path (it contains ':' and '/').
  const path = `/runtimes/${encodeURIComponent(runtimeArn)}/ws`;
  // SigV4 canonical URI for a non-S3 service is the URI-encoded form of the
  // ALREADY-encoded path, i.e. the encoded ARN segment is encoded a SECOND time
  // (%3A -> %253A, %2F -> %252F). The wss:// URL we actually open keeps the
  // single-encoded `path`, but the string we SIGN must use this double-encoded
  // form or the service recomputes a different signature and rejects the /ws
  // handshake ("signature does not match"). Verified byte-for-byte against botocore.
  const canonicalPath = `/runtimes/${rfc3986(encodeURIComponent(runtimeArn))}/ws`;
  const { amzDate, dateStamp } = amzDates(nowMs);
  const credentialScope = `${dateStamp}/${region}/${SERVICE}/aws4_request`;

  // All auth material rides in the query string for a presigned URL (the only
  // signed header is Host). Session-token included as X-Amz-Security-Token.
  //
  // NOTE: the session id is deliberately NOT a signed query param. The AgentCore
  // /ws data plane rejects the handshake ("signature does not match") unless the
  // canonical request is signed exactly as it is sent; the confirmed working recipe
  // (verified live against the deployed runtime) signs only `qualifier` + the
  // X-Amz-* params with SignedHeaders=host, and passes NO session id on connect.
  // The runtime mints its own per-connection session id when none is supplied
  // (agent/voice-runtime/app.py `_session_id`), so audit correlation is unaffected.
  const query = {
    qualifier: 'DEFAULT',
    'X-Amz-Algorithm': SIGV4_ALGORITHM,
    'X-Amz-Credential': `${accessKeyId}/${credentialScope}`,
    'X-Amz-Date': amzDate,
    'X-Amz-Expires': String(expiresIn),
    'X-Amz-Security-Token': sessionToken,
    'X-Amz-SignedHeaders': 'host',
  };

  // For a SigV4 QUERY-presigned GET the hashed payload is the SHA-256 of an EMPTY
  // body (the well-known e3b0c442... constant), NOT the literal 'UNSIGNED-PAYLOAD'
  // sentinel. The AgentCore data plane recomputes the signature with the empty-body
  // hash, so signing 'UNSIGNED-PAYLOAD' yields a mismatch and the /ws handshake is
  // rejected. Verified byte-for-byte against botocore's SigV4QueryAuth.
  const emptyBodyHash = await sha256Hex('');
  const canonicalRequest = [
    'GET',
    canonicalPath,
    canonicalQuery(query),
    `host:${host}\n`, // canonical headers (trailing newline required)
    'host', // signed headers
    emptyBodyHash,
  ].join('\n');

  const stringToSign = [
    SIGV4_ALGORITHM,
    amzDate,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join('\n');

  const key = await signingKey(secretAccessKey, dateStamp, region, SERVICE);
  const signature = toHex(await hmac(key, stringToSign));

  const signedQuery = `${canonicalQuery(query)}&X-Amz-Signature=${signature}`;
  return `wss://${host}${path}?${signedQuery}`;
}

// --- session id -------------------------------------------------------------

// AWS requires the runtime session id to be at least 33 characters. A UUID (36
// chars) satisfies that; fall back to a random hex string where randomUUID is
// unavailable (older/test environments).
export function generateVoiceSessionId() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(20));
    return `sess-${toHex(bytes)}`; // 5 + 40 = 45 chars
  }
  // Last-resort fallback (kept >= 33 chars); non-crypto, only if no WebCrypto RNG.
  return `sess-${Date.now().toString(16)}${Math.random().toString(16).slice(2).padEnd(24, '0')}`;
}

// --- Cognito Identity Pool credential exchange (network) --------------------

// Exchange the Cognito User Pool ID token for temporary AWS credentials via the
// Identity Pool: GetId then GetCredentialsForIdentity, with the user pool as the
// login provider. Done over plain `fetch` against the cognito-identity endpoint
// (JSON/1.1 target headers) so we don't add the AWS SDK to the bundle. Returns
// { accessKeyId, secretAccessKey, sessionToken, expiration }.
async function fetchTemporaryCredentials({ region, userPoolId, identityPoolId, idToken }) {
  const endpoint = `https://cognito-identity.${region}.amazonaws.com/`;
  const provider = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
  const logins = { [provider]: idToken };

  const call = async (target, body) => {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-amz-json-1.1',
        'x-amz-target': `AWSCognitoIdentityService.${target}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Don't surface the response body (may echo request material); just the op.
      throw new Error(`Cognito Identity ${target} failed (${res.status})`);
    }
    return res.json();
  };

  const { IdentityId } = await call('GetId', { IdentityPoolId: identityPoolId, Logins: logins });
  if (!IdentityId) throw new Error('Cognito Identity GetId returned no IdentityId');

  const { Credentials } = await call('GetCredentialsForIdentity', {
    IdentityId,
    Logins: logins,
  });
  if (!Credentials || !Credentials.AccessKeyId || !Credentials.SecretKey || !Credentials.SessionToken) {
    throw new Error('Cognito Identity GetCredentialsForIdentity returned incomplete credentials');
  }
  return {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    // Expiration is epoch SECONDS in the JSON/1.1 response.
    expiration: typeof Credentials.Expiration === 'number' ? Credentials.Expiration * 1000 : null,
  };
}

// Cache the temporary creds until shortly before they expire, so repeated presigns
// (reconnects) don't re-hit Cognito on every attempt. Mirrors the sample's caching.
let _cachedCreds = null;
let _cachedExpiryMs = null;
const CRED_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

// --- public API (consumed by task 7.1's voice screen) -----------------------

// True when the deployment has the voice config needed to presign (identity pool +
// runtime ARN). The voice screen uses this to decide voice-vs-typed (Req 11.6).
export function isVoicePresignConfigured() {
  return getVoiceConfig() !== null;
}

/**
 * Produce a fresh, short-lived presigned `wss://` URL for the voice runtime, plus
 * the session id it was signed for. This is the entry point task 7.1 calls right
 * before opening the WebSocket.
 *
 * Fetches temporary Identity Pool credentials (reusing getValidIdToken for the ID
 * token), then delegates to the pure buildPresignedVoiceWsUrl.
 *
 * @param {object} [opts]
 * @param {string} [opts.sessionId]  reuse a session id across reconnects
 * @returns {Promise<{ url: string, sessionId: string }>}
 * @throws if voice isn't configured, the user isn't authenticated, or the exchange fails
 */
export async function presignVoiceWebSocketUrl(opts = {}) {
  const voiceConfig = getVoiceConfig();
  if (!voiceConfig) {
    throw new Error('Voice is not configured on this deployment');
  }
  const { region, userPoolId, identityPoolId, voiceRuntimeArn } = voiceConfig;

  const idToken = await getValidIdToken(); // throws NotAuthenticatedError if signed out

  const nowMs = Date.now();
  let credentials = _cachedCreds;
  if (!credentials || !_cachedExpiryMs || _cachedExpiryMs - nowMs <= CRED_EXPIRY_BUFFER_MS) {
    credentials = await fetchTemporaryCredentials({
      region,
      userPoolId,
      identityPoolId,
      idToken,
    });
    _cachedCreds = credentials;
    _cachedExpiryMs = credentials.expiration;
  }

  const sessionId = opts.sessionId || generateVoiceSessionId();
  const url = await buildPresignedVoiceWsUrl({
    region,
    runtimeArn: voiceRuntimeArn,
    sessionId,
    credentials,
    expiresIn: VOICE_PRESIGN_EXPIRY_SECONDS,
    nowMs,
  });
  return { url, sessionId };
}

// Clear the cached temporary credentials — call on sign-out so a new user never
// reuses the previous identity's creds.
export function clearVoiceCredentials() {
  _cachedCreds = null;
  _cachedExpiryMs = null;
}
